import dayjs from 'dayjs'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { ModalBody, ModalFooter, Button } from 'reactstrap'

import * as NetworkErrorDialogSlice from 'slices/networkErrorDialogSlice'
import { selectSessionStatus } from 'slices/sessionSlice'
import { ERROR_STATUS_CODE, exportData } from 'slices/utils'

import { Modal } from 'components/common'

import { getConsoleLogs } from './utils'

const EMPTY_VALUE = '-'

type NetworkErrorDialogBodyProps = {
  code: string
  errorMessage: string
}

const NetworkErrorDialogBody = ({ code, errorMessage }: NetworkErrorDialogBodyProps) => {
  return (
    <ModalBody>
      {code === ERROR_STATUS_CODE.UNREACHABLE && (
        <div className="d-flex flex-column gap-4">
          <div className="font-large fw-bold">ネットワークエラー</div>
          <div>ネットワークに接続されていないため、アクセスできませんでした。接続した状態で再度お試しください。</div>
          <div>解決しない場合はエラー情報をダウンロードし、担当者へのご連絡をお願いします。</div>
        </div>
      )}
      {code === ERROR_STATUS_CODE.UNKNOWN && (
        <div className="d-flex flex-column gap-4">
          <div className="font-large fw-bold">予期せぬ不具合が発生しました</div>
          <div>問題が発生しています。エラー情報をダウンロードし、担当者へのご連絡をお願いします。</div>
          <div>エラーメッセージ：{errorMessage}</div>
        </div>
      )}
      {![ERROR_STATUS_CODE.UNREACHABLE, ERROR_STATUS_CODE.UNKNOWN].includes(code) && (
        <div className="d-flex flex-column gap-4">
          <div className="font-large fw-bold">問題が発生しました</div>
          <div>{errorMessage || '問題が発生しているため、しばらくしてから、再度アクセスしてください。'}</div>
          {!errorMessage && <div>エラーコード：{code}</div>}
          <div>解決しない場合はエラー情報をダウンロードし、担当者へのご連絡をお願いします。</div>
        </div>
      )}
    </ModalBody>
  )
}

export const NetworkErrorDialog = () => {
  const { show, code, errorMessage, requestTime, endpoint, method, path, requestId, responseMessage } = useSelector(
    NetworkErrorDialogSlice.selectNetworkErrorDialogStatus,
    shallowEqual
  )
  const { user } = useSelector(selectSessionStatus, shallowEqual)

  const dispatch = useDispatch()
  const close = () => {
    dispatch(NetworkErrorDialogSlice.close())
  }

  const buildErrorReport = (): string => {
    const message = responseMessage || errorMessage || EMPTY_VALUE
    const lines = [
      '==== エラー情報 ====',
      `発生時刻: ${requestTime || EMPTY_VALUE}`,
      `ユーザーID: ${user.userId || EMPTY_VALUE}`,
      `ページURL: ${window.location.href || EMPTY_VALUE}`,
      `メソッド: ${method || EMPTY_VALUE}`,
      `パス: ${path || endpoint || EMPTY_VALUE}`,
      `ステータスコード: ${code || EMPTY_VALUE}`,
      `x-amzn-RequestId: ${requestId || EMPTY_VALUE}`,
      `エラーメッセージ: ${message}`,
      '',
      '==== コンソールログ ====',
      getConsoleLogs(),
      '',
      '==== EOF ====',
      '',
    ]
    return lines.join('\n')
  }

  const downloadErrorReport = () => {
    const blob = new Blob([buildErrorReport()], { type: 'text/plain' })
    const stamp = dayjs().format('YYYYMMDD_HHmmss')
    exportData(blob, `errorReport_${stamp}.txt`)
  }

  return (
    <Modal isOpen={show} zIndex={3000}>
      <NetworkErrorDialogBody code={code} errorMessage={errorMessage} />
      <ModalFooter className="d-flex justify-content-end">
        <Button color="primary" onClick={downloadErrorReport}>
          エラー情報をダウンロード
        </Button>
        <Button outline onClick={close}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}
